'use client';

import { BigNum } from '@drift-labs/sdk';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import Input from 'src/components/primitives/Input/Input';
import ValueDisplay from 'src/components/ValueDisplay';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useSubaccountCollateralBalanceForBank from 'src/hooks/useSubaccountCollateralBalanceForBank';
import React, { useEffect, useRef, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useAccountCreationCost } from 'src/hooks/useAccountCreationCost';
import useTransferableWalletCollateralBalance from 'src/hooks/useTransferableWalletCollateralBalance';
import { MAIN_POOL_ID, MIN_LEFTOVER_SOL, UIMarket } from '@drift/common';
import { WALLET_OPTION_VALUE } from 'src/constants/constants';
import { MaxSubaccountsWarning } from '../../Utils/MaxSubaccountsWarning';
import { useMaxSubaccountsReached } from 'src/hooks/useMaxSubaccountsReached';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { POOL_NAMES_BY_POOL_ID } from '../../../constants/constants';
import AirdropButton from '../../Buttons/AirdropButton';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import ConnectWalletButtonsList from 'src/components/ConnectWalletButtonsList/index';
import useHandleSelectWalletProvider from 'src/hooks/useHandleSelectWalletProvider';
import { PublicKey } from '@solana/web3.js';
import useTotalWalletQuoteBalance from 'src/hooks/useTotalWalletQuoteBalance';
import { NewSubaccountModalProps } from 'src/components/Modals/CollateralModal/types';
import useModal from 'src/components/Modals/hooks/useModal';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import Button from 'src/components/primitives/Button/Button';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import Divider from 'src/components/primitives/Divider/Divider';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { CollateralSourceSelector } from 'src/components/Inputs/CollateralSourceSelector';
import { useCreateNewSubaccount } from 'src/hooks/driftActions/user/useCreateNewSubaccount';
import { DepositFromOtherSourcesContent } from './components/DepositFromOtherSourcesContent';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { cj } from 'src/utils/classNames';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

// TODO - This should come from SDK
const maxChars = 32;

const ConnectionStepContent = () => {
	const { handleSelectProvider } = useHandleSelectWalletProvider();

	return (
		<div>
			<ConnectWalletButtonsList
				showUndetectedWallets={false}
				onSelectedWallet={(walletName) => {
					handleSelectProvider(walletName);
				}}
			/>
		</div>
	);
};

const NewSubaccountModal = ({
	collateralIndex,
	collateralPoolId,
	depositAccountSelection: initialDepositAccountSelection,
	depositAmount: initialDepositAmount,
	customAccountName: initialCustomAccountName,
}: NewSubaccountModalProps) => {
	const { hide, push } = useModal();

	const intentPoolId = collateralPoolId ?? MAIN_POOL_ID;
	const showNewPoolAccountCreationMessage = intentPoolId !== MAIN_POOL_ID;

	const availableSubAccounts = useLazySubAccounts();
	const intentCollateralIndex = collateralIndex ?? 0;
	const { captureEvent } = usePostHogCapture();
	const solBalance = useDriftStore((s) => s.wallet.currentSolBalance);
	const [customAccountName, setCustomAccountName] = useState(
		initialCustomAccountName ?? ''
	);
	const isMobile = useIsMobileScreenSize();
	const createNewSubaccount = useCreateNewSubaccount();
	const isDepositFromOtherSourcesEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_DEPOSIT_FROM_OTHER_SOURCES')
	);
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const initialSelectedMarket =
		intentCollateralIndex === null
			? spotMarketsSortedByMarketIndex[0]
			: spotMarketsSortedByMarketIndex[intentCollateralIndex];

	const [depositAccountSelection, setDepositAccountSelection] = useState(() => {
		if (initialDepositAccountSelection) {
			return initialDepositAccountSelection;
		}

		if (isPrivyNativeAuth) {
			return availableSubAccounts[0]?.userKey ?? '';
		}

		return WALLET_OPTION_VALUE;
	});

	const [selectedCollatMarket, setSelectedCollatMarket] = useState(
		initialSelectedMarket
	);
	const selectedCollateralUISpotMarketConfig = UIMarket.createSpotMarket(
		selectedCollatMarket.marketIndex
	);
	const [depositAmount, setDepositAmount] = useState(initialDepositAmount);
	const [submitting, setSubmitting] = useState(false);

	const subaccountBalance = useSubaccountCollateralBalanceForBank({
		accountKey: depositAccountSelection,
		market: selectedCollatMarket,
		useTokenBalance: true,
	});
	const userStatsAccount = useDriftStore((s) => s.userStatsAccount);
	const currentAuthority = useCurrentWalletAdapter()?.publicKey;
	const subaccountCount = userStatsAccount?.numberOfSubAccountsCreated ?? 0;
	const maxSubaccountReached = useMaxSubaccountsReached();
	const isDevMode = useDevSwitchIsOn();

	// Only allow a custom account name if it's the main pool and they already have subaccounts
	const allowCustomName = intentPoolId === MAIN_POOL_ID && subaccountCount > 0;

	const {
		totalCost: totalAccountCreationCost,
		loaded: accountCreationCostLoaded,
	} = useAccountCreationCost();

	const handleDepositAmountChange = (newValue?: string) => {
		// Set input to max amount if exceeds limit
		if (newValue && newValue !== '') {
			const inputBigNum = BigNum.fromPrint(
				newValue,
				selectedCollatMarket.precisionExp
			);
			if (inputBigNum.lte(currentCollatBalanceBigNum)) {
				setDepositAmount(newValue);
			} else {
				// Set to max amount if input exceeds limit
				setDepositAmount(currentCollatBalanceBigNum.print());
			}
		} else {
			setDepositAmount(newValue);
		}
	};

	const [walletDepositableBalanceBigNum] =
		useTransferableWalletCollateralBalance(selectedCollatMarket, true);

	const { totalQuoteBalance } = useTotalWalletQuoteBalance();

	const isWalletSelected = depositAccountSelection === WALLET_OPTION_VALUE;

	const currentCollatBalanceBigNum = isWalletSelected
		? walletDepositableBalanceBigNum
		: subaccountBalance;

	const depositAmountBigNum = BigNum.fromPrint(
		depositAmount ? depositAmount : '0',
		selectedCollatMarket.precisionExp
	);

	const defaultNewAccountName = `Subaccount ${(subaccountCount ?? 0) + 1}`;

	const balanceExceeded = depositAmountBigNum.gt(currentCollatBalanceBigNum);

	const isMaxSolDeposit =
		depositAmount !== '' &&
		isWalletSelected &&
		depositAmountBigNum.eq(walletDepositableBalanceBigNum) &&
		selectedCollatMarket.symbol === 'SOL';

	const charsRemaining = maxChars - customAccountName.length;

	const canCreateMoreAccounts =
		isDevMode ||
		Env.maxAccountsPerWallet >
			availableSubAccounts.filter((acct) =>
				acct?.authority?.equals(currentAuthority ?? PublicKey.default)
			)?.length;

	// Reset transfer amount if market selection changes (skip initial mount to preserve restored state)
	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		setDepositAmount('');
	}, [selectedCollatMarket]);

	const handleAddAccount = async () => {
		if (!canCreateMoreAccounts) return;

		setSubmitting(true);

		const accountName =
			allowCustomName && customAccountName ? customAccountName : undefined; // If the user hasn't selected a custom name - we can leave this undefined because the Drift Client and action handler will handle this

		captureEvent('creating_new_subaccount');

		const fundsSource = depositAccountSelection
			? availableSubAccounts.find(
					(acct) => acct.userKey == depositAccountSelection
				)?.userId
			: null;

		try {
			const success = await createNewSubaccount({
				depositAmount: depositAmountBigNum,
				spotMarketIndex: selectedCollatMarket.marketIndex,
				poolId: selectedCollatMarket.poolId,
				accountName,
				fromSubAccountId: fundsSource ?? undefined,
			});

			if (success) {
				hide();
			}
		} catch (error) {
			console.log('Error creating new subaccount', error);
		} finally {
			setSubmitting(false);
		}
	};

	const handleDepositFromOtherSources = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		push({
			type: 'deposit_from_other_sources',
			props: {
				collateralIndex: intentCollateralIndex,
				collateralPoolId: intentPoolId,
				createNewSubaccount: true,
			},
		});
	};

	const isWalletConnected = useWalletIsConnected();

	const showDepositFromOtherSources =
		isWalletConnected &&
		canCreateMoreAccounts &&
		isDepositFromOtherSourcesEnabled;

	const showDesktopDepositFromOtherSources =
		!isMobile && showDepositFromOtherSources;

	return (
		<BaseModal.Container
			onClose={hide}
			className={cj(
				'sm:w-104',
				showDesktopDepositFromOtherSources &&
					'overflow-visible bg-transparent shadow-none'
			)}
			dataTestId="create-subaccount-modal"
		>
			<div className="flex h-full flex-col overflow-hidden sm:rounded-sm sm:shadow-md">
				<BaseModal.Title
					onClose={hide}
					closeButtonTestId="create-subaccount-modal-close"
				>
					{isWalletConnected ? 'Add account' : 'Connect wallet'}
				</BaseModal.Title>

				<BaseModal.Body>
					{isWalletConnected ? (
						<>
							{isDevMode && <AirdropButton spotMarket={selectedCollatMarket} />}

							{!canCreateMoreAccounts ? (
								<div className="flex flex-col items-center gap-y-xl text-center">
									<span className="typo-t3 text-text-default">
										This wallet has reached the maximum number of subaccounts
										(8).
									</span>
									<Button
										size="lg"
										className="w-full"
										onClick={hide}
										dataTestId="create-subaccount-modal-done"
									>
										Done
									</Button>
								</div>
							) : (
								<>
									{showNewPoolAccountCreationMessage && (
										<InlineAlert variant="info" title="New Subaccount Required">
											Depositing into the{' '}
											{
												POOL_NAMES_BY_POOL_ID[
													intentPoolId as keyof typeof POOL_NAMES_BY_POOL_ID
												]
											}{' '}
											requires creating a new subaccount. Your funds will be
											deposited into this new subaccount.
										</InlineAlert>
									)}

									{allowCustomName && (
										<div className="flex flex-col gap-y-2xs">
											<Input
												id="new-account-name"
												labelVariant="standard"
												label="New Account name"
												type="text"
												value={customAccountName}
												onChange={setCustomAccountName}
												placeholder={defaultNewAccountName}
												disabled={!canCreateMoreAccounts}
												dataTestId="new-account-name-input"
											/>
											<div className="flex items-center justify-between">
												<span className="typo-b5">
													{`${customAccountName.length}/${maxChars}`}
												</span>
											</div>

											{charsRemaining < 0 && (
												<InlineAlert
													variant="warning"
													title="Invalid account name"
												>
													You can&apos;t exceed the account name character limit
												</InlineAlert>
											)}
										</div>
									)}

									<CollateralSourceSelector
										label="Deposit Collateral From"
										initialSelection={depositAccountSelection}
										onChange={(accountKey) => {
											setDepositAccountSelection(accountKey);
										}}
										options={availableSubAccounts}
										walletBalance={totalQuoteBalance}
										includeDelegates
									/>

									<CollateralInputV2
										selectedMarket={selectedCollatMarket}
										maxAmount={currentCollatBalanceBigNum}
										onChangeMarket={setSelectedCollatMarket}
										label="Amount"
										value={depositAmount}
										defaultToHighestBalance={!initialDepositAmount}
										onChangeValue={handleDepositAmountChange}
										source={
											isWalletSelected
												? {
														type: 'wallet',
													}
												: {
														type: 'userAccount',
														userKey: depositAccountSelection,
													}
										}
										poolId={intentPoolId}
										customElementRight={
											<InputQuickActions.SizePresets
												onClick={(amount) => setDepositAmount(amount.print())}
												maxAmount={currentCollatBalanceBigNum}
											/>
										}
										searchBodyWithApy={isWalletSelected}
										fullScreenPopover={isMobile}
										fullScreenPopoverTitle="Select Asset"
									/>

									{balanceExceeded && (
										<InlineAlert variant="error" title="Invalid deposit amount">
											You can&apos;t deposit more than your available account
											balance
										</InlineAlert>
									)}

									{isMaxSolDeposit && (
										<span>
											We hold back {MIN_LEFTOVER_SOL.toFixed(2)} SOL on max
											deposits to cover transaction fees
										</span>
									)}

									{maxSubaccountReached && <MaxSubaccountsWarning />}

									<Divider />

									<ValueDisplay.ValueChange
										label="Asset Balance"
										previousValue={currentCollatBalanceBigNum}
										afterValue={BigNum.max(
											currentCollatBalanceBigNum.sub(depositAmountBigNum),
											BigNum.zero(selectedCollatMarket.precisionExp)
										)}
										previousValuePrint={`${currentCollatBalanceBigNum.prettyPrint()} ${
											selectedCollateralUISpotMarketConfig.uiSymbols
												.baseAssetDisplaySymbol
										}`}
										afterValuePrint={`${currentCollatBalanceBigNum
											.sub(depositAmountBigNum)
											.prettyPrint()} ${
											selectedCollateralUISpotMarketConfig.uiSymbols
												.baseAssetDisplaySymbol
										}`}
									/>
								</>
							)}
						</>
					) : (
						<ConnectionStepContent />
					)}
				</BaseModal.Body>

				{isWalletConnected && canCreateMoreAccounts && (
					<BaseModal.Footer className="flex-col items-stretch">
						<Button
							size="lg"
							className="w-full"
							onClick={handleAddAccount}
							disabled={
								!accountCreationCostLoaded ||
								solBalance.lt(totalAccountCreationCost) ||
								!canCreateMoreAccounts ||
								submitting ||
								!depositAmount ||
								depositAmountBigNum.lte(
									BigNum.zero(selectedCollatMarket.precisionExp),
									true
								) ||
								balanceExceeded ||
								maxSubaccountReached ||
								(allowCustomName && customAccountName.length > maxChars)
							}
							isLoading={submitting}
							dataTestId="create-subaccount-modal-submit"
						>
							{canCreateMoreAccounts ? 'Add' : `Unavailable`}
						</Button>
						<span>
							Reclaimable account setup fee:{' '}
							<NumberDisplay
								type="number"
								value={totalAccountCreationCost}
								decimals={2}
								suffix="SOL"
							/>
						</span>
					</BaseModal.Footer>
				)}
			</div>

			{showDepositFromOtherSources && (
				<div
					className={cj(
						isMobile ? 'border-t border-stroke-divider p-4' : 'pt-xs'
					)}
				>
					<button
						className={cj(
							'flex h-17 w-full items-center gap-x-sm rounded-sm px-lg text-text-default',
							isMobile
								? 'bg-button-secondary-bg hover:bg-button-secondary-bg-hover'
								: 'bg-container-bg shadow-md hover:bg-input-bg'
						)}
						onClick={handleDepositFromOtherSources}
					>
						<DepositFromOtherSourcesContent />
					</button>
				</div>
			)}
		</BaseModal.Container>
	);
};

export default React.memo(NewSubaccountModal);
